import React, { useState, useEffect, useMemo } from 'react';
import { getItems } from '../api/services';
import { formatCurrency, getPriceListDisplayName } from '../Utils/formatters';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const getHeaderDisplayName = (header) => {
  if (header === 'UnidadMedida') return 'UNM';
  if (header === 'Existencia') return 'Cant';
  if (header === 'LINEAS') return 'Linea';
  if (header.startsWith('PrecioLista')) {
    return getPriceListDisplayName(header);
  }
  return header;
};

function ViewData({ setFeedback }) {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        setIsLoading(true);
        const itemsData = await getItems();
        const processedItems = itemsData.map(item => ({
          ...item,
          UnidadMedida: item.UnidadMedida || item.UnidadInventario || 'UND'
        }));
        setItems(processedItems);
      } catch (error) {
        console.error('Error fetching items:', error);
        setFeedback({ message: 'Error al cargar los datos de la tabla.', type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllItems();
  }, [setFeedback]);

  const sortedAndFilteredItems = useMemo(() => {
    let filtered = [...items];
    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      filtered = items.filter(item =>
        Object.values(item).some(val =>
          String(val).toLowerCase().includes(lowercasedFilter)
        )
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (sortConfig.key.startsWith('PrecioLista')) {
           return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        }

        if (aValue.toString().toLowerCase() < bValue.toString().toLowerCase()) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue.toString().toLowerCase() > bValue.toString().toLowerCase()) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return filtered;
  }, [items, searchTerm, sortConfig]);

  const tableHeaders = useMemo(() => {
    if (items.length === 0) return [];
    const allKeys = items.reduce((keys, item) => {
      Object.keys(item).forEach(key => keys.add(key));
      return keys;
    }, new Set());
    
    const headers = Array.from(allKeys);
    const fixedOrder = ['Referencia', 'Descripcion', 'UnidadMedida', 'Existencia'];
    const priceHeaders = headers.filter(h => h.startsWith('PrecioLista')).sort();
    const otherHeaders = headers.filter(h => !fixedOrder.includes(h) && !h.startsWith('PrecioLista'));
    
    return [...fixedOrder.filter(h => headers.includes(h)), ...otherHeaders, ...priceHeaders];
  }, [items]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'ascending' ? 'descending' : 'ascending'
    }));
  };

  if (isLoading) {
    return <section><h2>Datos de Productos</h2><div className="spinner" /></section>;
  }

  return (
    <section>
      <h2>Datos de Productos</h2>
      <div className="search-wrapper">
        <input
          type="text"
          placeholder="Buscar en la tabla..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="table-wrapper">
        {items.length > 0 ? (
          <table>
            <thead>
              <tr>
                {tableHeaders.map(header => (
                  <th key={header} onClick={() => handleSort(header)} style={{ cursor: 'pointer' }}>
                    {getHeaderDisplayName(header)}
                    <span className="sort-indicator">
                      {sortConfig.key === header
                        ? (sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />)
                        : <FaSort />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredItems.map((item) => (
                <tr key={item.Referencia}>
                  {tableHeaders.map(header => (
                    <td key={`${item.Referencia}-${header}`}>
                      {header.startsWith('PrecioLista') ? formatCurrency(item[header]) : item[header]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No hay productos para mostrar.</p>
        )}
      </div>
    </section>
  );
}

export default ViewData;